import { Tab, Tabs } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle } from "utils/customHooks/currencyFormat";

const projectOverviewHeader = [
  {
    id: "title",
    title: "Title",
    align: "left",
  },
  {
    id: "value",
    title: "Value",
    align: "right",
  },
];

const generateMortgagePosition = (mp) => {
  return {
    first: "1",
    second: "2",
    "third+": "3+",
  }[mp];
};

export const UnderWritingSummeryTable = ({
  netOperatingIncome,
  estimatedAssetValue,
}) => {
  const form = useFormContext();
  const [
    loanType,
    projectPurpose,
    assetClasses,
    address,
    refinanceLoan,
    netAmountRequested,
    mortgagePosition,
    loanTerms,
    loanStructure,
    amortizationPeriod,
    interestRate,
    premium,
    proposedInterestRate,
    annualDebtService,
    existingDebtOnProperty,
    totalDebtAmount,
    totalCashEquity,
    additionalRequiredCashEquity,
    annualDebts,
    existingDebts,
    capRate,
  ] = useWatch({
    name: [
      "loan_type",
      "project_purpose",
      "asset_classes",
      "address",
      "refinance_loan",
      "net_amount_requested",
      "mortgage_position",
      "loan_terms",
      "loan_structure",
      "amortization_period",
      "interest_rate",
      "premium",
      "proposed_interest_rate",
      "total_annual_debt_service",
      "existing_debt_on_property",
      "total_debt_amount",
      "total_cash_equity",
      "cash_equity_difference",
      "annual_debts",
      "existing_debts",
      "cap_rate",
    ],
    control: form.control,
  });
  const projectOverviewRows = [
    {
      title: "Loan Type",
      value: loanType + (refinanceLoan ? " (Refinance)" : ""),
    },
    {
      title: "Project Purpose",
      value: projectPurpose,
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
    {
      title: "Asset Class",
      value: assetClasses ? assetClasses.join(", ") : "",
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
    {
      title: "Location",
      value: [
        address.address,
        address.city,
        address.state_or_province + ` (${address.postal_code})`,
      ].join(", "),
    },
  ];

  let loanRequestRows = [
    {
      title: "Requested Loan Amount",
      value: `$ ${currencyStyle(netAmountRequested)}`,
    },
    {
      title: "Mortgage Position",
      value: generateMortgagePosition(mortgagePosition),
    },
    {
      title: `Loan Term` + (loanTerms ? ` (${loanTerms.unit}s)` : ""),
      value: loanTerms ? loanTerms.count : "",
    },
    {
      title: "Loan Structure",
      value: loanStructure,
      props: {
        value: {
          className: "capitalize",
        },
      },
    },
  ];
  if (loanStructure !== "interest only") {
    loanRequestRows.push({
      title:
        `Amortization Period` +
        (amortizationPeriod ? ` (${amortizationPeriod.unit}s)` : ""),
      value: amortizationPeriod ? amortizationPeriod.count : "",
    });
  }
  let interestRateView = interestRate ? interestRate : undefined;
  if (interestRateView && premium && proposedInterestRate === "variable")
    interestRateView += premium;
  loanRequestRows = [
    ...loanRequestRows,
    {
      title: "Interest Rate",
      value: `${interestRateView ? interestRateView + "%" : ""} ${
        proposedInterestRate ? proposedInterestRate : ""
      }`,
      props: {
        value: {
          className: "uppercase",
        },
      },
    },
    {
      title: "Total Annual Debt Service",
      value: annualDebtService
        ? `$ ${currencyStyle(annualDebtService)}`
        : "$ -",
    },
  ];
  const annualDebtRequestedLoan = (annualDebts || []).find(
    (row) => row.title === "Requested Loan"
  );

  (annualDebts || [])
    .filter((row) => ["Requested Loan"].indexOf(row.title) === -1)
    .forEach((row) =>
      loanRequestRows.push({
        title: row.title,
        value: `$ ${currencyStyle(row.amount)}`,
        props: {
          title: {
            className: "text-gray bg-cell",
          },
          value: {
            className: "text-gray bg-cell",
          },
        },
      })
    );

  if (annualDebtRequestedLoan)
    loanRequestRows.push({
      title: "Requested Loan",
      value: `$ ${currencyStyle(annualDebtRequestedLoan.amount)}`,
      props: {
        title: {
          className: "text-gray bg-cell",
        },
        value: {
          className: "text-gray bg-cell",
        },
      },
    });

  let financialAnalysisRows = [
    {
      title: "Existing Debt on Property",
      value: `$ ${currencyStyle(existingDebtOnProperty)}`,
    },
  ];

  (existingDebts || []).forEach((row) =>
    financialAnalysisRows.push({
      title: row.title,
      value: `$ ${currencyStyle(row.amount)}`,
      props: {
        title: {
          className: "text-gray bg-cell",
        },
        value: {
          className: "text-gray bg-cell",
        },
      },
    })
  );

  const LTV = !isNaN(totalDebtAmount / estimatedAssetValue)
    ? Number(((totalDebtAmount / estimatedAssetValue) * 100).toFixed(2))
    : "";
  financialAnalysisRows = [
    ...financialAnalysisRows,
    {
      title: "Total Debt Amount",
      value: `$ ${currencyStyle(totalDebtAmount)}`,
    },
    {
      title: "Total Cash Equity",
      value: `$ ${currencyStyle(totalCashEquity)}`,
    },
    {
      title: "Estimated Asset Value",
      value: `$ ${currencyStyle(estimatedAssetValue)}`,
    },
    {
      title: "LTV",
      value:
        typeof LTV !== "undefined" && LTV !== null && LTV !== ""
          ? LTV + "%"
          : "",
      props: {
        title: {},
        value: {},
      },
    },
    {
      title: "Cap Rate",
      value: typeof capRate !== "undefined" && capRate ? capRate + "%" : "",
      props: {
        title: {},
        value: {},
      },
    },
    {
      title:
        additionalRequiredCashEquity < 0
          ? "Eligible Equity Takeout"
          : "Additional Required Cash Equity",
      value: `$ ${currencyStyle(
        Number(Math.abs(additionalRequiredCashEquity).toFixed(2))
      )}`,
      props: {
        title: {},
        value: {},
      },
    },
  ];

  const netOpRow = [
    {
      title: "Net Operating Income",
      value: `$ ${currencyStyle(netOperatingIncome)}`,
      props: {
        title: {},
      },
    },
  ];

  const residualTables = [];
  const residualTableFooters = [];
  let lastResidualNOI = netOperatingIncome;
  (annualDebts || [])
    .filter((row) => row.title !== "Requested Loan")
    .forEach((row) => {
      residualTables.push([
        {
          title: row.title + " Debt Service",
          value: `$ ${currencyStyle(-row.amount)}`,
        },
        {
          title: "DSCR",
          value: currencyStyle(
            Number((lastResidualNOI / row.amount).toFixed(2))
          ),
          props: {
            title: {
              className: "text-gray bg-cell",
            },
            value: {
              className: "text-gray bg-cell",
            },
          },
        },
      ]);
      residualTableFooters.push([
        {
          title: "Residual NOI",
          value: `$ ${currencyStyle(
            Number((lastResidualNOI - row.amount).toFixed(2))
          )}`,
        },
      ]);
      lastResidualNOI = lastResidualNOI - row.amount;
    });

  if (Array.isArray(residualTables) && residualTables[0]) {
    residualTables[0] = [netOpRow[0], ...residualTables[0]];
  } else {
    residualTables[0] = netOpRow;
  }

  const annualDebtServiceRows = annualDebtRequestedLoan
    ? [
        {
          title: "Annual Debt Service",
          value: `$ ${currencyStyle(-annualDebtRequestedLoan.amount)}`,
        },
      ]
    : [];

  const annualDebtServiceFooter = [
    {
      title: "DSCR",
      value: currencyStyle(
        !isNaN(lastResidualNOI / annualDebtRequestedLoan.amount)
          ? Number(
              (lastResidualNOI / annualDebtRequestedLoan.amount).toFixed(2)
            )
          : "-"
      ),
    },
  ];
  return (
    <Tabs activeKey={"underwriting"} className="mt-4">
      <Tab
        eventKey={"underwriting"}
        title="Underwriting Summary"
        tabClassName="capitalize text-left"
      >
        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Project Overview</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={projectOverviewRows}
          />
        </div>
        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Loan Request</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={loanRequestRows}
          />
        </div>

        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Financial Analysis</span>
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={financialAnalysisRows}
          />
        </div>
        {residualTables.map((rows, i) => {
          return (
            <div className="mb-2" key={i++}>
              <Table
                sameColumnWidth
                hideHeader
                horizontal
                headers={projectOverviewHeader}
                rows={rows}
                footer={residualTableFooters[i]}
              />
            </div>
          );
        })}

        <div className="mb-2">
          <Table
            sameColumnWidth
            hideHeader
            horizontal
            headers={projectOverviewHeader}
            rows={annualDebtServiceRows}
            footer={annualDebtServiceFooter}
          />
        </div>
      </Tab>
    </Tabs>
  );
};
